import { DownloadSimpleIcon, XIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Entity } from '../types/apiTypes';
import { useUser } from '../utils/UserContext';
import { safeGetItem, safeSetItem } from '../shared/safeStorage';

const IOS_URL =
  'https://apps.apple.com/us/app/tach-for-truckers/id6738364119?l=pt-BR';
const ANDROID_URL =
  'https://play.google.com/store/apps/details?id=com.tach.money.prod';

export const MobileAppPrompt = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { userRecord } = useUser();

  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  const isAndroid = /android/i.test(userAgent);
  // Detect Safari (excludes Chrome/CriOS and FxiOS)
  const isSafari =
    /^((?!chrome|android).)*safari/i.test(userAgent) &&
    !/crios/i.test(userAgent) &&
    !/fxios/i.test(userAgent);

  useEffect(() => {
    // Check user type
    if (
      !userRecord ||
      (userRecord.userType !== Entity.Customer &&
        userRecord.userType !== Entity.Driver)
    ) {
      return;
    }

    if (!isIOS && !isAndroid) {
      return;
    }

    // Do NOT show custom modal on Safari (native banner takes over)
    if (isIOS && isSafari) {
      return;
    }

    // Custom Modal for iOS & Android
    if (isAndroid || isIOS) {
      // Check if user has already dismissed the prompt
      const hasDismissed = safeGetItem('tach_mobile_app_prompt_dismissed');
      if (hasDismissed) return;

      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [userRecord, isIOS, isAndroid, isSafari]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleDismiss = () => {
    safeSetItem('tach_mobile_app_prompt_dismissed', 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-end justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity"
      onClick={handleDismiss}
    >
      <div
        className="w-full rounded-t-2xl bg-white p-6 pb-8 shadow-xl transition-transform duration-300 md:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/app-icon.png"
              alt="TACH App Icon"
              className="h-12 w-12 rounded-xl shadow-md"
            />
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Get the full experience
              </h3>
              <p className="text-sm text-gray-500">
                Download the TACH mobile app
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XIcon size={24} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <a
            href={isIOS ? IOS_URL : ANDROID_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3.5 font-semibold text-white shadow-sm hover:bg-primary/90 active:scale-[0.98]"
          >
            <DownloadSimpleIcon size={20} weight="bold" />
            <span>Download for {isIOS ? 'iOS' : 'Android'}</span>
          </a>
        </div>

        <button
          onClick={handleDismiss}
          className="mt-4 w-full text-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          Not now
        </button>
      </div>
    </div>,
    document.body
  );
};

export default MobileAppPrompt;
